import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import cacheManager from '../CacheManager';
import { formatDate } from '../js/dateFormatter';

const PgRelFinanceiro = () => {
  
  const [clientesByIdCache, setClientesByIdCache] = useState({});
  const [itensById, setItensById] = useState({});
  const [agendamentos, setAgendamentos] = useState([]);
  
  const [pagamentos, setPagamentos] = useState([]);

  // Função para obter o primeiro dia do mês anterior
  const getFirstDayOfPreviousMonth = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    date.setDate(1);
    return date.toISOString().split('T')[0];
  };

  // Função para obter o último dia do mês anterior
  const getLastDayOfPreviousMonth = () => {
    const date = new Date();
    date.setMonth(date.getMonth(), 0);
    return date.toISOString().split('T')[0];
  };

  const [dataInicial, setDataInicial] = useState(getFirstDayOfPreviousMonth());
  const [dataFinal, setDataFinal] = useState(getLastDayOfPreviousMonth());
  const [filtroCliente, setFiltroCliente] = useState('');
  const [faturasFiltradas, setFaturasFiltradas] = useState([]);
  const [faturasOrdenadas, setFaturasOrdenadas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Função para normalizar string (remove acentos, lowercase, trim)
  const normalizeString = (str) => {
    if (!str) return '';
    return str
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  const search = useCallback(async () => {
    try {
      setIsLoading(true);

      const clientesByIdCache = await cacheManager.getClientesById()
      setClientesByIdCache(clientesByIdCache);
      
      const itensById = await cacheManager.getItensById();
      setItensById(itensById);
      
      const agendamentosCache = await cacheManager.getAgendamentosCache();
      setAgendamentos(agendamentosCache);
      
      const faturasCache = await cacheManager.getFaturasCache();
      setPagamentos(faturasCache);

    } catch (error) {
      console.error('Erro ao buscar faturas:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Estilos para esmaecer a página
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 2,
    display: isLoading ? 'block' : 'none',
  };

  useEffect(() => {
    search();
  }, [search, dataInicial, dataFinal]);

  useEffect(() => {
    const faturasFiltradas = pagamentos.filter(fatura => {

      // Determinar qual data usar
      let dataParaUsar = fatura.data_pagamento;
      
      // Verificar se data_pagamento é válida (não é '0000-00-00' e não é NaN)
      if (!dataParaUsar || dataParaUsar.startsWith('0000-00-00') || isNaN(new Date(dataParaUsar).getTime())) {
        dataParaUsar = null;
      }
      
      // Se não tiver data_pagamento válida, verificar se pode usar data_emissao
      if (!dataParaUsar && fatura.data_emissao) {
        const dataEmissao = new Date(fatura.data_emissao).toISOString().split('T')[0];
        if (dataEmissao >= '2025-01-01' && dataEmissao <= '2025-06-01') {
          dataParaUsar = fatura.data_emissao;
        }
      }

      if (!dataParaUsar || isNaN(new Date(dataParaUsar).getTime())) {
        // Só mostra warn se a fatura estiver no período válido mas não tiver data utilizável
        if (fatura.data_emissao) {
          const dataEmissao = new Date(fatura.data_emissao).toISOString().split('T')[0];
          if (dataEmissao >= '2025-01-01') {
            console.warn(`Pagamento inválido (período válido):`, fatura);
            console.warn(`data_emissao: ${fatura.data_emissao}, data_pagamento: ${fatura.data_pagamento}`);
          }
        }
        return false; // Exclui pagamentos inválidos
      }

      const dataPagamento = new Date(dataParaUsar).toISOString().split('T')[0];
      const dataInicialDate = dataInicial ? new Date(dataInicial).toISOString().split('T')[0] : null;
      const dataFinalDate = dataFinal ? new Date(dataFinal).toISOString().split('T')[0] : null;
      
      if (dataInicialDate && dataPagamento < dataInicialDate) {
        return false;
      }

      if (dataFinalDate && dataPagamento > dataFinalDate) {
        return false;
      }

      // Filtrar por nome do cliente
      if (filtroCliente) {
        const nomeCliente = clientesByIdCache[fatura.id_cliente]?.nome || '';
        const filtroNormalizado = normalizeString(filtroCliente);
        const nomeNormalizado = normalizeString(nomeCliente);
        
        if (!nomeNormalizado.includes(filtroNormalizado)) {
          return false;
        }
      }

      return true;
    });

    const faturasOrdenadas = [...faturasFiltradas].sort((a, b) => {
      // Determinar qual data usar para 'a'
      let dataA = a.data_pagamento;
      
      // Verificar se data_pagamento é válida (não é '0000-00-00' e não é NaN)
      if (!dataA || dataA.startsWith('0000-00-00') || isNaN(new Date(dataA).getTime())) {
        dataA = null;
      }
      
      if (!dataA && a.data_emissao) {
        const dataEmissaoA = new Date(a.data_emissao).toISOString().split('T')[0];
        if (dataEmissaoA >= '2025-01-01' && dataEmissaoA <= '2025-06-01') {
          dataA = a.data_emissao;
        }
      }

      // Determinar qual data usar para 'b'
      let dataB = b.data_pagamento;
      
      // Verificar se data_pagamento é válida (não é '0000-00-00' e não é NaN)
      if (!dataB || dataB.startsWith('0000-00-00') || isNaN(new Date(dataB).getTime())) {
        dataB = null;
      }
      
      if (!dataB && b.data_emissao) {
        const dataEmissaoB = new Date(b.data_emissao).toISOString().split('T')[0];
        if (dataEmissaoB >= '2025-01-01' && dataEmissaoB <= '2025-06-01') {
          dataB = b.data_emissao;
        }
      }

      if (!dataA || !dataB) {
        console.warn('Erro na ordenação - data inválida:', { 
          faturaA: a.id_fatura, 
          dataA, 
          data_pagamento_a: a.data_pagamento,
          data_emissao_a: a.data_emissao,
          faturaB: b.id_fatura, 
          dataB,
          data_pagamento_b: b.data_pagamento,
          data_emissao_b: b.data_emissao
        });
      }

      const aX = new Date(dataA);
      const bX = new Date(dataB);

      if (aX.getTime() === bX.getTime()){
        return a.id_fatura - b.id_fatura; // Ordena por ID se as datas forem iguais
      }

      return bX - aX;
    });

  

  setFaturasFiltradas(faturasFiltradas);
  setFaturasOrdenadas(faturasOrdenadas);
  }, [pagamentos, dataInicial, dataFinal, filtroCliente, clientesByIdCache]);

  // Calcular total de registros e somatório do valor
  const totalRegistros = faturasFiltradas.length;
  const somatorioValor = faturasFiltradas.reduce((total, fatura) => total + parseFloat(fatura.valor_total), 0);

  // Formatar o somatório do valor
  const somatorioValorFormatado = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(somatorioValor);

  return (
    <div className="container mt-5">
      {isLoading && <div style={overlayStyle}></div>}
      <h2 className="mb-4">Faturas emitidas</h2>
      <div className="input-group mb-3">
        <div className="form-group">
          <label htmlFor="data-inicial">Data Inicial</label>
          <input
            type="date"
            className="form-control"
            id="data-inicial"
            value={dataInicial}
            onChange={(e) => setDataInicial(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="data-final">Data Final</label>
          <input
            type="date"
            className="form-control"
            id="data-final"
            value={dataFinal}
            onChange={(e) => setDataFinal(e.target.value)}
          />
        </div>
        <div className="form-group" style={{ minWidth: '300px' }}>
          <label htmlFor="filtro-cliente">Cliente</label>
          <input
            type="text"
            className="form-control"
            id="filtro-cliente"
            placeholder="Filtrar por nome do cliente"
            value={filtroCliente}
            onChange={(e) => setFiltroCliente(e.target.value)}
          />
        </div>
      </div>
      <table className="table">
        <thead className="table-light">
          <tr>
            <th>Nº Fatura</th>
            <th>Cliente</th>
            <th>Agendamentos</th>
            <th>Itens</th>
            <th>Data Pag.</th>
            <th>Valor</th>
          </tr>
        </thead>
        <tbody>
          {faturasOrdenadas.map((fatura, index) => (
            <tr key={index} style={{ height: 'auto !important', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <td>
                <Link to={`/fatura/editar/${fatura.id_fatura}`} className="text-dark underline-on-hover">
                  {fatura.id_fatura}
                </Link>
              </td>
              <td>{clientesByIdCache[fatura.id_cliente]?.nome || 'Cliente não encontrado'}</td>
              <td>
                {agendamentos
                  .filter(ag => String(ag.id_fatura) === String(fatura.id_fatura))
                  .map(ag => itensById[ag.id_item]?.titulo || 'Item não encontrado')
                  .join(', ')}
              </td>
              <td>
                {(() => {
                  const itensJson = JSON.parse(fatura.itens_json || '[]');
                  return Array.isArray(itensJson) ? itensJson.map(item => item.nome).join(', ') : '';
                })()}
              </td>
              <td>
                {(() => {
                  let dataExibir = fatura.data_pagamento;
                  if (!dataExibir || dataExibir.startsWith('0000-00-00')) {
                    // Só usa data_emissao se estiver entre 2025-01-01 e 2025-06-01
                    if (fatura.data_emissao) {
                      const dataEmissao = new Date(fatura.data_emissao).toISOString().split('T')[0];
                      if (dataEmissao >= '2025-01-01' && dataEmissao <= '2025-06-01') {
                        dataExibir = fatura.data_emissao;
                      }
                    }
                  }
                  return formatDate(dataExibir);
                })()}
              </td>
              <td>R$ {fatura.valor_total}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3">
        <p>Total de Registros: {totalRegistros}</p>
        <p>Valor total das fatuas: {somatorioValorFormatado}</p>
      </div>
    </div>
  );
};

export default PgRelFinanceiro;